/* React */
import React from "react";

/* Utils */
import ChartWrapper from "src/utils/wrapper/chart-wrapper/ChartWrapper";

/* Components */
import SurvivalPlot from "src/components/charts/survival-plot/SurvivalPlot";
import Table from "src/components/shared/table/Table";
import SurvivalPlotOptions from "src/components/chart-wrapper/toolbar/options/survival-plot-options/SurvivalPlotOptions";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";
import {
  excludeCohortNoValueGroups,
  orderCohortGroups,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { DescriptiveStatisticsProps } from "src/shared/interfaces/DescriptiveStatistics";
import { SurvivalAnalysisProps } from "src/shared/interfaces/SurvivalAnalysis";
import { SurvivalPlotProps } from "src/shared/interfaces/charts/SurvivalPlot";
import { TableProps } from "src/shared/interfaces/charts/Table";

export const createSurvivalPlot = (
  index: number,
  label: React.ReactNode,
  data: BaseChartProps<
    DescriptiveStatisticsProps | SurvivalAnalysisProps,
    ChartType[]
  >,
  legend: React.ReactNode,
  downloadFilename?: string,
  showNoValueEntries?: boolean
) => {
  // Cohort no-value groups ("Not looked for" / "Looked for, no value") are
  // always fetched; the toggle only controls display. Hide them when off, and
  // order them after the real cohorts - consistently for the curves, the plot
  // and the event table - so their (name-based grey) colours line up with the
  // legend, which applies the same filtering/ordering.
  const scatter: SurvivalPlotProps[] =
    data.data[ChartType.SCATTER];
  const eventTableData: TableProps[] =
    data.data[ChartType.TABLE];

  const curves = orderCohortGroups(
    excludeCohortNoValueGroups(
      scatter,
      showNoValueEntries ?? false
    )
  );
  const eventTable = orderCohortGroups(
    excludeCohortNoValueGroups(
      eventTableData,
      showNoValueEntries ?? false
    )
  );

  return [
    {
      component: (
        <ChartWrapper
          label={label}
          options={<SurvivalPlotOptions />}
          legend={legend}
          downloadFilename={downloadFilename}
        >
          {(height, width) => (
            <SurvivalPlot
              key={`survival-plot-${index}`}
              data={curves}
              table={eventTable}
              dimensions={{ height, width }}
            />
          )}
        </ChartWrapper>
      ),
    },
    {
      component: (
        <Table
          key={`table-${index}`}
          index={index}
          label={"Event Table"}
          stacked={true}
          data={eventTable}
          downloadFilename={
            downloadFilename
              ? `${downloadFilename}-event-table`
              : undefined
          }
        />
      ),
    },
  ];
};
